import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { useCallback } from "react";
import {
    useAppDispatch,
    useAppSelector
} from "../../../../Hooks/useStore";

export type RealtimeStatus =
    | "idle"
    | "connecting"
    | "connected"
    | "disconnected"
    | "error";

export type VoiceState =
    | "idle"
    | "listening"
    | "thinking"
    | "speaking";

export type RealtimeInitialStateTypes = {

    status: RealtimeStatus;

    sessionId: string | null;

    isSpeaking: boolean;

    voiceState: VoiceState;

    lastUserText: string;

    lastAssistantText: string;

    error: string | null;

};


const initialState: RealtimeInitialStateTypes = {
    status: "idle",
    sessionId: null,
    isSpeaking: false,
    voiceState: "idle",
    lastUserText: "",
    lastAssistantText: "",
    error: null,
};


export const realtimeSlice = createSlice({
    name: "realtimeSlice",
    initialState,
    reducers: {
        setConnecting: (state) => {
            state.status = "connecting";
            state.error = null;
        },
        setConnected: (
            state,
            action: PayloadAction<string | null>
        ) => {
            state.status = "connected";
            state.sessionId = action.payload;
            state.error = null;
            state.voiceState = "idle";
        },
        setDisconnected: (state) => {
            state.status = "disconnected";
            state.sessionId = null;
            state.isSpeaking = false;
            state.voiceState = "idle";
        },
        setSpeaking: (
            state,
            action: PayloadAction<boolean>
        ) => {
            state.isSpeaking = action.payload;
            state.voiceState =
                action.payload
                    ? "speaking"
                    : "idle";
        },
        setVoiceState: (
            state,
            action: PayloadAction<VoiceState>
        ) => {
            state.voiceState = action.payload;
            state.isSpeaking =
                action.payload === "speaking";
        },
        setLastUserText: (
            state,
            action: PayloadAction<string>
        ) => {
            state.lastUserText = action.payload;
        },
        setLastAssistantText: (
            state,
            action: PayloadAction<string>
        ) => {
            state.lastAssistantText = action.payload;
        },
        setError: (
            state,
            action: PayloadAction<string>
        ) => {
            state.status = "error";
            state.error = action.payload;
        },
        reset: () => initialState,
    },

});


export const {
    setConnecting,
    setConnected,
    setDisconnected,
    setSpeaking,
    setVoiceState,
    setLastUserText,
    setLastAssistantText,
    setError,
    reset,
} = realtimeSlice.actions;

export const useRealtime = () => {
    const realtime = useAppSelector(
        (state) => state.realtime
    );
    const dispatch = useAppDispatch();
    const connecting = useCallback(() => {
        dispatch(setConnecting());
    }, [dispatch]);
    const connected = useCallback(
        (sessionId: string | null) => {
            dispatch(
                setConnected(sessionId)
            );

        },
        [dispatch]
    );
    const disconnected = useCallback(() => {
        dispatch(setDisconnected());
    }, [dispatch]);
    const speaking = useCallback(
        (value: boolean) => {
            dispatch(
                setSpeaking(value)
            );
        },
        [dispatch]
    );
    const changeVoiceState = useCallback(
        (state: VoiceState) => {
            dispatch(
                setVoiceState(state)
            );
        },
        [dispatch]
    );
    const updateUserText = useCallback(
        (text: string) => {

            dispatch(
                setLastUserText(text)
            );
        },
        [dispatch]
    );
    const updateAssistantText = useCallback(
        (text: string) => {
            dispatch(setLastAssistantText(text));
        },
        [dispatch]
    );
    const setErrorState = useCallback(
        (error: string) => {
            dispatch(setError(error));
        },
        [dispatch]
    );
    const clear = useCallback(() => {
        dispatch(reset());
    }, [dispatch]);
    return {
        ...realtime,
        isConnected: realtime.status === "connected",
        isConnecting: realtime.status === "connecting",
        connecting,
        connected,
        disconnected,
        speaking,
        changeVoiceState,
        updateUserText,
        updateAssistantText,
        setError: setErrorState,
        clear,
    };
};

export default realtimeSlice.reducer;